<template>
  <form class="vl-form" @submit.prevent="confirm()">
    <vl-grid v-if="entityModel" v-vl-spacer:bottom.medium>
      <vl-column width="12" width-s="12" width-xs="12">
        <vl-properties-list v-if="entityModel">
          <vl-properties-label>{{ $t('entitymodel.detail.id') }}</vl-properties-label>
          <vl-properties-data>{{ entityModel.id }}</vl-properties-data>
          <vl-properties-label>{{ $t('entitymodel.detail.code') }}</vl-properties-label>
          <vl-properties-data>{{ entityModel.code }}</vl-properties-data>
          <vl-properties-label>{{ $t('entitymodel.detail.label') }}</vl-properties-label>
          <vl-properties-data>{{ entityModel.label }}</vl-properties-data>
        </vl-properties-list>
      </vl-column>
      <vl-column v-if="!isLoading" width="12" width-s="12" width-xs="12">
        <vl-action-group v-vl-spacer:top.medium>
          <vl-button :mod-disabled="isSaving || !canDelete" :mod-loading="isSaving">{{
            $t('general.delete')
          }}</vl-button>
          <vl-link
            :mod-disabled="isSaving"
            :mod-loading="isSaving"
            mod-button
            mod-secondary
            target="_blank"
            :to="`/configuration/entitymodel/${props.id}/dependencies`"
          >
            {{ $t('entitymodel.detail.dependencies') }}
          </vl-link>
          <vl-button :mod-disabled="isSaving" mod-secondary @click.prevent="cancel">
            {{ $t('general.cancel') }}
          </vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <template v-if="isLoading">
      <vl-region>
        <div v-vl-align:center>
          <vl-loader :message="$t('general.formLoading')" />
        </div>
      </vl-region>
    </template>
    <template v-if="error">
      <vl-region>
        <vl-alert icon="warning" :title="$t('general.formLoadingError')" :content="error" mod-naked mod-error />
      </vl-region>
    </template>
  </form>
</template>
<script lang="ts" setup>
import { computed, onMounted } from 'vue';
import { useEntityModelDetailStore } from '@/modules/configuration/store/entity-model/entity-model-detail.store';
import { useEntityModelListStore } from '@/modules/configuration/store/entity-model/entity-model-list.store';
import { reactive } from 'vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { parseException } from '@/modules/core/store/alert.store';
import { useRoute, useRouter } from 'vue-router';

const detailStore = useEntityModelDetailStore();
const listStore = useEntityModelListStore();

const router = useRouter();
const route = useRoute();

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
});

const data = reactive({
  entityModel: null,
  isLoading: false,
  isSaving: false,
  canDelete: false,
  error: null,
});

onMounted(async () => {
  try {
    data.isLoading = true;
    await loadEntityModel();
  } catch (error) {
    data.error = parseException(error);
  } finally {
    data.isLoading = false;
  }
});

const loadEntityModel = async () => {
  const entityModel = await detailStore.getDetail(props.id);
  if (entityModel) {
    data.entityModel = entityModel;
    data.canDelete = await detailStore.validateCascadeDelete(props.id);
  }
};

const entityModel = computed(() => {
  return data.entityModel;
});

const isLoading = computed(() => {
  return data.isLoading;
});

const isSaving = computed(() => {
  return data.isSaving;
});

const canDelete = computed(() => {
  return data.canDelete;
});

const error = computed(() => {
  return data.error;
});

const confirm = async () => {
  const isSuccess = await cascadeDelete();
  if (isSuccess) {
    listStore.getModels();

    if (route.name == 'entitymodel.detail') {
      router.push({ name: 'configuration', hash: '#entitymodel' });
    }
  }

  closeModal();
};

const cascadeDelete = async (): Promise<boolean> => {
  if (data.isSaving) {
    return;
  }

  let isSuccess = false;
  try {
    data.isSaving = true;
    isSuccess = await detailStore.cascadeDelete(props.id);
  } catch (e) {
    console.error(e);
  } finally {
    data.isSaving = false;
  }
  return isSuccess;
};

const cancel = () => {
  closeModal();
};

const closeModal = () => {
  useModalStore().hideModal();
};
</script>
<style lang="scss" scoped></style>
